import { formatDistanceToNowStrict } from 'date-fns';
import type { CurrentRegistrar } from '../types';

const parse = (v: string) => new Date(v.includes('T') || v.length === 10 ? v : `${v.replace(' ', 'T')}Z`);

/** "Ours" / "Third party" / "Unconfirmed" for where a transferred domain went. */
export function DestinationBadge({ destination }: { destination: CurrentRegistrar['destination'] }) {
  if (!destination) return null;
  const meta = {
    ours: { label: 'Ours', tone: 'bg-green-50 text-green-700 border-green-200', title: 'Moved to a registrar we have accounts at' },
    third_party: { label: 'Third party', tone: 'bg-red-50 text-red-700 border-red-200', title: 'Now held outside our accounts' },
    unknown: { label: 'Unconfirmed', tone: 'bg-slate-100 text-slate-600 border-slate-200', title: 'Waiting for a WHOIS lookup after the move' },
  }[destination];
  return <span title={meta.title} className={`inline-flex items-center rounded-full border px-1.5 py-px text-[11px] font-medium ${meta.tone}`}>{meta.label}</span>;
}

/**
 * The registrar to show for a domain. Held: the account's registrar (from the
 * registrar API). Not held / moved: the registrar from WHOIS, marked derived,
 * with the account it left — never the old account as if it still held it.
 */
export default function RegistrarLabel({ fallback, current, compact = false }: { fallback: string; current?: CurrentRegistrar; compact?: boolean }) {
  if (!current || current.source === 'registrar_api') return <span>{fallback}</span>;
  const prev = current.previous ? `${current.previous.registrar_name} (${current.previous.label})` : fallback;
  const checked = current.whois_checked_at ? `WHOIS checked ${formatDistanceToNowStrict(parse(current.whois_checked_at), { addSuffix: true })}` : null;
  const title = [current.whois_registrar ? `WHOIS registrar: ${current.whois_registrar}` : null, checked, `Previously ${prev}${current.previous?.until ? ` until ${current.previous.until.slice(0, 10)}` : ''}`, current.destination_detail]
    .filter(Boolean).join('\n');
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5" title={title}>
      {current.source === 'whois'
        ? <span className="italic">{current.name}</span>
        : <span className="italic text-slate-400">Unknown</span>}
      <span className="rounded bg-slate-100 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-slate-500">{current.source === 'whois' ? 'WHOIS' : 'pending'}</span>
      <DestinationBadge destination={current.destination} />
      {!compact && <span className="text-xs text-slate-400">previously {prev}{current.previous?.until ? ` until ${current.previous.until.slice(0, 10)}` : ''}</span>}
    </span>
  );
}
